// The on-navigate reset registry (contract 05 §4/§16, ORP-18611) — the second half of the
// query-callback seam. v3 opened its mega-`beforeEach` by reaching into four feature stores to
// drop per-page state (router/index.js:51–52,63,68–69: `clearDashboardWs`,
// `refreshPageRecommendations`, report cache `clear`, `resetRequests`). Navigation must not know
// those features exist, so each owner (08/09/15) contributes its reset here instead and the shell
// runs them. This module holds ZERO resets; owners add theirs on their own tickets.
//
// Registration is app-side, for the same reason as `queryCallbackRegistry`: features may not
// import `app/` (eslint `boundaries/dependencies`).

import type { NavigationHookAfter, RouteLocationNormalized } from 'vue-router'

/**
 * The confirmed destination is passed in so a contribution can act on WHERE the navigation landed,
 * not just that one happened — `to.redirectedFrom` is the only place a redirect's origin survives.
 * Contributions that ignore it keep their `() => void` shape, which stays assignable.
 */
export type NavReset = (to: RouteLocationNormalized) => void

const resets = new Set<NavReset>()

/** Contribute a reset to run on every confirmed navigation. Re-registering the same function is a no-op. */
export function registerNavReset(reset: NavReset): void {
  resets.add(reset)
}

/**
 * `router.afterEach` hook: run every contributed reset. This is an afterEach, not a guard —
 * contract §11 fixes the beforeEach chain at exactly cancelInFlight → auth → permission, and a
 * reset is a side-effect, never a navigation decision. It still lands before the incoming page's
 * `onMounted`, preserving v3's "stale state is gone before the new page fetches" ordering.
 *
 * afterEach also fires for navigations that never happened (a guard returned false, or one nav
 * superseded another): `failure` is set and the user is still on the old page, whose state these
 * resets would otherwise wipe. A redirect is not a failure — the resets run on the final nav.
 */
export const runNavResets: NavigationHookAfter = (to, _from, failure) => {
  if (failure) return
  for (const reset of resets) reset(to)
}

/** Drop every registration. Exists so tests start from an empty registry. */
export function resetNavResets(): void {
  resets.clear()
}
